import React, { useState, useEffect } from 'react';
import { AdditionalServicesDetails, formatAsDate, getAdditionalServicesCertificate } from '@cp-mx/common';
import { useAnalyticsActionTracker } from '../../../../../analytics';
import { AccordionItem, DefinitionListHorizontal, DefinitionListItem, Spinner } from '@cp-shared-19/frontend-ui';
import { useComponentTracking } from '../../../../../analytics/useComponentTracking';
import { useTranslation } from 'react-i18next';
import { Button, Notification, Modal } from '@vwfs-bronson/bronson-react';
import { CpDataApi } from '../../../../../cp-xhr';
import { ErrorNotification } from '../../../../notifications/error/ErrorNotification';

const ModalError: React.FC<{
    shown: boolean;
    onClose: () => void;
}> = ({ shown, onClose }) => {
    const { t } = useTranslation('additional-services-details');
    return (
        <Modal className={'c-modal'} style={{ height: '80%' }} shown={shown} onClose={() => onClose()}>
            <Notification
                testId={'error-notification'}
                status="error"
                title={t('error.noConnection.title')}
                visible={true}
                showCloseButton={false}
            >
                {t('error.noConnection.text')}
            </Notification>
        </Modal>
    );
};

const ModalIframe: React.FC<{
    shown: boolean;
    url: string;
    onClose: () => void;
}> = ({ shown, url, onClose }) => {
    const { t } = useTranslation('additional-services-details');
    return (
        <Modal
            className={'c-modal  c-modal--large'}
            style={{ height: '80%' }}
            shown={shown}
            testId={'show-modal'}
            onClose={() => onClose()}
        >
            {!url ? (
                <>
                    <Spinner center />
                    {t('loadSpinnerText')}
                </>
            ) : (
                <div style={{ height: '500px' }}>
                    <iframe title="Policy" style={{ width: '100%', height: '100%' }} src={url}></iframe>
                </div>
            )}
        </Modal>
    );
};

export const AdditionalServicesDetailsUi: React.FC<{
    additionalServicesDetails?: AdditionalServicesDetails[];
    title?: string;
    contractLabel: string;
    isLoading?: boolean;
}> = ({ additionalServicesDetails, title, contractLabel, isLoading }) => {
    const { t } = useTranslation('additional-services-details');
    const [width, setWidth] = useState(window.innerWidth);
    const [isLoadingButton, setIsLoadingButton] = useState<boolean>(false);
    const [shownErrorModal, setShownErrorModal] = useState(false);
    const [shownIframeModal, setShownIframeModal] = useState(false);
    const [url, setUrl] = useState('');
    const { onAction: onOpenAccordion } = useAnalyticsActionTracker('onOpenContractAccordion');
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { onAction: onSuccess } = useAnalyticsActionTracker('onRequestCertificateSuccess');
    const { onAction: onError } = useAnalyticsActionTracker('onRequestCertificateSubmitFailed');
    const { onAction: onApiError } = useAnalyticsActionTracker('onRequestCertificateSubmitAPIError');
    const errorNotification = (
        <ErrorNotification
            testId={'no-connection-error'}
            headline={t('error.noConnection.title')}
            text={t('error.noConnection.text')}
        />
    );
    const handleResize = () => {
        setWidth(window.innerWidth);
    };

    useEffect(() => {
        window.addEventListener('resize', handleResize);
    }, []);

    const contractAdditionalServices = additionalServicesDetails
        ? additionalServicesDetails.length > 0
            ? additionalServicesDetails.find(item => item.contractNumber === contractLabel)
            : undefined
        : undefined;
    useComponentTracking(
        `contract-additional-services-details-${contractLabel}`,
        contractAdditionalServices?.status === 'Certificado',
        'contracts',
        'Accordion',
    );
    if (contractAdditionalServices?.status !== 'Certificado') {
        return null;
    }
    const getListItem = (additionalServicesDetails: AdditionalServicesDetails): DefinitionListItem[] => {
        const items: DefinitionListItem[] = [];
        if (!additionalServicesDetails) {
            return items;
        }
        if (additionalServicesDetails.product) {
            items.push({
                label: t('productName'),
                value: t('productNameValue'),
                testId: 'productName',
            });
        }
        if (additionalServicesDetails.product.name) {
            items.push({
                label: t('coverage'),
                value: additionalServicesDetails.product.name,
                testId: 'coverage',
            });
        }
        if (additionalServicesDetails.id) {
            items.push({
                label: t('certificate'),
                value: additionalServicesDetails.id,
                testId: 'certificate',
            });
        }
        if (additionalServicesDetails.vehicle.isUsed) {
            const issuanceDate = additionalServicesDetails.issuanceDate.split('T');
            items.push({
                label: t('validityDate'),
                value: formatAsDate(issuanceDate[0], false, true),
                testId: 'validityDateUsedCar',
            });
        } else {
            items.push({
                label: t('validityDate'),
                value: formatAsDate(additionalServicesDetails.sagaDate, false, true),
                testId: 'validityDate',
            });
        }
        return items;
    };

    const getCertificate = async (certificate: number, action: string): Promise<string> => {
        setIsLoadingButton(true);
        return await CpDataApi.get(getAdditionalServicesCertificate(certificate.toString()))
            .then(response => {
                setIsLoadingButton(false);
                setUrl(response.data.signedUrl);
                if (action === 'download') {
                    onSuccess('Maintenance Certificate - download');
                } else {
                    onSuccess('Maintenance Certificate - display');
                }
                return response.data.signedUrl;
            })
            .catch(error => {
                if (action === 'download') {
                    onError('Maintenance Certificate - download', error);
                    onApiError('Maintenance Certificate - download', error);
                } else {
                    onError('Maintenance Certificate - display', error);
                    onApiError('Maintenance Certificate - display', error);
                }
                setShownIframeModal(false);
                setIsLoadingButton(false);
                setShownErrorModal(true);
                return '';
            });
    };
    const startDownload = async (url: string) => {
        if (url) {
            window.open(url);
        }
    };

    const getCertificateDownload = async (certificate: number, action: string) => {
        setIsLoadingButton(true);
        if (!url) {
            await getCertificate(certificate, action).then(response => {
                return startDownload(response);
            });
        } else {
            if (!shownErrorModal) {
                onSuccess('Maintenance Certificate - download');
                startDownload(url);
            }
            setIsLoadingButton(false);
        }
    };

    const showCertificate = async (certificateId: number, action: string) => {
        setShownIframeModal(true);
        if (!url) {
            await getCertificate(certificateId, action);
        } else {
            onSuccess('Maintenance Certificate - display');
        }
    };

    const displayLoadingSpinner = () => {
        const certificate = contractAdditionalServices ? contractAdditionalServices?.id : 0;
        const button = (
            <>
                <Button
                    className={width > 821 ? 'u-mu-p' : 'u-mh'}
                    full={!(width > 821)}
                    testId={'download-button'}
                    onClick={() => {
                        getCertificateDownload(certificate, 'download');
                    }}
                >
                    {!isLoadingButton ? (
                        <>
                            {' '}
                            <i className="c-icon  c-icon--[download]" role="img" aria-hidden="true" />
                        </>
                    ) : (
                        <Spinner small center />
                    )}
                </Button>
            </>
        );
        return button;
    };

    return (
        <>
            {isLoading && <Spinner center />}
            {!additionalServicesDetails && <AccordionItem title={title}>{errorNotification}</AccordionItem>}
            {contractAdditionalServices ? (
                <>
                    <ModalIframe shown={shownIframeModal} url={url} onClose={() => setShownIframeModal(false)} />
                    <ModalError shown={shownErrorModal} onClose={() => setShownErrorModal(false)} />
                    <div
                        style={{ borderTop: '1px solid #4c5356' }}
                        onClick={() => {
                            if (!isOpen) {
                                onOpenAccordion('My Additional Products');
                            }
                            setIsOpen(!isOpen);
                        }}
                    >
                        <AccordionItem title={title}>
                            <DefinitionListHorizontal list={getListItem(contractAdditionalServices)} />
                            {width > 821 ? (
                                <>
                                    <div className={'o-layout--right u-mt'}>
                                        <Button
                                            className={'u-mr'}
                                            secondary
                                            testId={'modal-button'}
                                            onClick={() => {
                                                showCertificate(contractAdditionalServices.id, 'visualize');
                                            }}
                                        >
                                            {t('visualizeButton')}
                                        </Button>
                                        {displayLoadingSpinner()}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Button
                                        full
                                        className={'u-m'}
                                        secondary
                                        testId={'modal-button'}
                                        onClick={() => {
                                            showCertificate(contractAdditionalServices.id, 'visualize');
                                        }}
                                    >
                                        {t('visualizeButton')}
                                    </Button>
                                    {displayLoadingSpinner()}
                                </>
                            )}
                        </AccordionItem>
                    </div>
                </>
            ) : (
                <></>
            )}
        </>
    );
};
